// frontend/src/pages/Dashboard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiUser,
  FiPackage,
  FiHeart,
  FiSettings,
  FiLogOut,
  FiShoppingBag,
  FiDollarSign,
  FiMail,
  FiPhone,
  FiCalendar,
  FiEdit2,
  FiSave,
  FiX,
  FiLock,
  FiEye,
  FiEyeOff,
  FiHome,
  FiMapPin,
  FiGlobe,
  FiBell,
  FiShield,
  FiSmartphone,
  FiMail as FiMailIcon,
  FiDatabase,
  FiTrash2,
  FiSearch,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiRefreshCw,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import api from "../services/api";
import GoogleAddressAutocomplete from "./GoogleAddressAutocomplete";
import AddressMapPicker from "./AddressMapPicker";
import "../styles/dashboard.css";

const Dashboard = () => {
  const {
    user,
    loading: authLoading,
    logout,
    resendVerification,
    refreshUser,
  } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [resending, setResending] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [profileData, setProfileData] = useState({
    userName: user?.name || user?.userName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    addressLine1: user?.addressLine1 || "",
    addressLine2: user?.addressLine2 || "",
    city: user?.city || "",
    state: user?.state || "",
    postalCode: user?.postalCode || "",
    country: user?.country || "South Africa",
  });
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderFilter, setOrderFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [orderStats, setOrderStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
  });

  // Helper function to preserve case sensitivity
  const formatNameCaseSensitive = (name) => {
    if (!name) return "";
    return name;
  };

  // Get the first letter for avatar (uppercase for consistency)
  const getAvatarLetter = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  // Get first name for welcome message
  const getFirstName = (fullName) => {
    if (!fullName) return "Guest";
    const firstName = fullName.split(" ")[0];
    return formatNameCaseSensitive(firstName);
  };

  // Check if user is social login
  const isSocialLogin = user?.isSocialLogin === true;

  // Password Change State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Account Deletion State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    sms: true,
    promotional: false,
    orderUpdates: true,
  });

  // Privacy settings
  const [privacy, setPrivacy] = useState({
    showEmail: true,
    showPhone: false,
  });

  // Country list
  const countries = [
    "South Africa",
    "United States",
    "United Kingdom",
    "Canada",
    "Australia",
    "Germany",
    "France",
    "Nigeria",
    "Kenya",
    "Egypt",
    "Other",
  ];

  // South African provinces
  const saProvinces = [
    "Eastern Cape",
    "Free State",
    "Gauteng",
    "KwaZulu-Natal",
    "Limpopo",
    "Mpumalanga",
    "North West",
    "Northern Cape",
    "Western Cape",
  ];

  // Order status config
  const statusConfig = {
    Pending: {
      icon: FiClock,
      color: "#e67e22",
      bg: "#fff3e0",
      label: "Pending",
    },
    Processing: {
      icon: FiRefreshCw,
      color: "#2196f3",
      bg: "#e3f2fd",
      label: "Processing",
    },
    Shipped: {
      icon: FiTruck,
      color: "#4caf50",
      bg: "#e8f5e9",
      label: "Shipped",
    },
    Delivered: {
      icon: FiCheckCircle,
      color: "#2ecc71",
      bg: "#e8f5e9",
      label: "Delivered",
    },
    Cancelled: {
      icon: FiXCircle,
      color: "#f44336",
      bg: "#ffebee",
      label: "Cancelled",
    },
  };

  // Redirect admin users to admin panel
  useEffect(() => {
    if (user && user.role === "Admin") {
      navigate("/admin");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role !== "Admin") {
      fetchOrders();
      loadUserProfile();
      loadUserSettings();

      const interval = setInterval(() => {
        fetchOrders(true);
      }, 30000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Filter orders
  useEffect(() => {
    let filtered = [...orders];

    if (orderFilter !== "all") {
      filtered = filtered.filter(
        (order) => order.Status?.toLowerCase() === orderFilter.toLowerCase(),
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.OrderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.Status?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredOrders(filtered);
  }, [orders, orderFilter, searchTerm]);

  const fetchOrders = async (silent = false) => {
    try {
      const response = await api.get("/api/orders/my-orders");
      const ordersData = response.data.orders || [];
      setOrders(ordersData);
      setFilteredOrders(ordersData);

      const stats = {
        total: ordersData.length,
        pending: ordersData.filter((o) => o.Status === "Pending").length,
        processing: ordersData.filter((o) => o.Status === "Processing").length,
        shipped: ordersData.filter((o) => o.Status === "Shipped").length,
        delivered: ordersData.filter((o) => o.Status === "Delivered").length,
        cancelled: ordersData.filter((o) => o.Status === "Cancelled").length,
      };
      setOrderStats(stats);

      if (!silent) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await api.get("/api/auth/me");
      if (response.data) {
        setProfileData({
          userName: response.data.name || user?.name || user?.userName || "",
          email: response.data.email || user?.email || "",
          phone: response.data.phone || user?.phone || "",
          addressLine1: response.data.addressLine1 || user?.addressLine1 || "",
          addressLine2: response.data.addressLine2 || user?.addressLine2 || "",
          city: response.data.city || user?.city || "",
          state: response.data.state || user?.state || "",
          postalCode: response.data.postalCode || user?.postalCode || "",
          country: response.data.country || user?.country || "South Africa",
        });
      }
    } catch (error) {
      console.error("Error loading user profile:", error);
    }
  };

  const loadUserSettings = async () => {
    try {
      const response = await api.get("/api/user/settings");
      if (response.data.success) {
        const { notifications: savedNotifications, privacy: savedPrivacy } =
          response.data.settings;
        setNotifications(savedNotifications);
        setPrivacy(savedPrivacy);
      }
    } catch (error) {
      console.error("Error loading user settings:", error);
      loadNotificationSettingsFallback();
      loadPrivacySettingsFallback();
    }
  };

  const loadNotificationSettingsFallback = () => {
    const savedEmail = localStorage.getItem("notify_email") === "true";
    const savedSms = localStorage.getItem("notify_sms") === "true";
    const savedPromotional =
      localStorage.getItem("notify_promotional") === "true";
    const savedOrderUpdates =
      localStorage.getItem("notify_order_updates") !== "false";
    setNotifications({
      email: savedEmail !== false,
      sms: savedSms !== false,
      promotional: savedPromotional === true,
      orderUpdates: savedOrderUpdates !== false,
    });
  };

  const loadPrivacySettingsFallback = () => {
    const savedShowEmail = localStorage.getItem("show_email") !== "false";
    const savedShowPhone = localStorage.getItem("show_phone") === "true";
    setPrivacy({
      showEmail: savedShowEmail,
      showPhone: savedShowPhone,
    });
  };

  const saveUserSettings = async (type, setting, value) => {
    try {
      const allSettings = {
        notifications,
        privacy,
      };

      if (type === "notifications") {
        allSettings.notifications[setting] = value;
      } else if (type === "privacy") {
        allSettings.privacy[setting] = value;
      }

      await api.put("/api/user/settings", { settings: allSettings });
    } catch (error) {
      console.error("Error saving user settings:", error);
    }
  };

  const handleUpdateProfile = async () => {
    if (!profileData.phone) {
      toast.error("Phone number is required");
      return;
    }
    if (!profileData.addressLine1) {
      toast.error("Street address is required");
      return;
    }
    if (!profileData.city) {
      toast.error("City is required");
      return;
    }
    if (!profileData.state) {
      toast.error("State/Province is required");
      return;
    }
    if (!profileData.postalCode) {
      toast.error("Postal code is required");
      return;
    }
    if (!profileData.country) {
      toast.error("Country is required");
      return;
    }

    try {
      await api.put("/api/auth/profile", {
        ...profileData,
        userName: profileData.userName,
        name: profileData.userName,
      });

      toast.success("Profile updated successfully");
      setIsEditing(false);

      const updatedUser = {
        ...user,
        name: profileData.userName,
        userName: profileData.userName,
        phone: profileData.phone,
        addressLine1: profileData.addressLine1,
        addressLine2: profileData.addressLine2,
        city: profileData.city,
        state: profileData.state,
        postalCode: profileData.postalCode,
        country: profileData.country,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("storage"));
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const result = await resendVerification();
      if (result.success) {
        toast.success("Verification email sent! Please check your inbox.");
      }
    } catch (error) {
      console.error("Resend verification error:", error);
      toast.error("Failed to send verification email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    setCheckingStatus(true);
    try {
      await refreshUser();
      const updatedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (updatedUser.emailVerified) {
        toast.success("Email verified! You now have full access.");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.info("Email not verified yet. Please check your inbox.");
      }
    } catch (error) {
      console.error("Check verification status error:", error);
      toast.error("Failed to check verification status");
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleAddressFromGoogle = (addressData) => {
    console.log("📍 Address received from Google:", addressData);

    setProfileData({
      ...profileData,
      addressLine1: addressData.addressLine1,
      city: addressData.city,
      state: addressData.state,
      postalCode: addressData.postalCode,
      country: addressData.country || "South Africa",
    });

    toast.success("Address found! Please review the details below.", {
      duration: 4000,
      icon: "📍",
    });
  };

  const handleAddressFromMap = (addressData) => {
    setProfileData({
      ...profileData,
      addressLine1: addressData.addressLine1,
      city: addressData.city,
      state: addressData.state,
      postalCode: addressData.postalCode,
      country: addressData.country || "South Africa",
    });

    toast.success(
      "Address selected from map! Please review the details below.",
      {
        duration: 4000,
        icon: "🗺️",
      },
    );
  };

  const handleChangePassword = async () => {
    setPasswordError("");

    if (!passwordData.currentPassword) {
      setPasswordError("Current password is required");
      return;
    }
    if (!passwordData.newPassword) {
      setPasswordError("New password is required");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setIsChangingPassword(true);
    setPasswordLoading(true);

    try {
      await api.put("/api/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success("Password changed successfully");
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordError("");
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to change password";
      setPasswordError(message);
      toast.error(message);
    } finally {
      setPasswordLoading(false);
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") {
      toast.error('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete("/api/auth/account");
    } catch (error) {
      console.error("Delete account error:", error);
      if (!error.response?.data?.forceLogout) {
        const message =
          error.response?.data?.message || "Failed to deactivate account";
        toast.error(message);
      }
      setShowDeleteModal(false);
      setDeleteConfirmation("");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNotificationChange = async (type) => {
    const newValue = !notifications[type];
    setNotifications({ ...notifications, [type]: newValue });
    await saveUserSettings("notifications", type, newValue);
    toast.success(
      `${type.charAt(0).toUpperCase() + type.slice(1)} notifications ${newValue ? "enabled" : "disabled"}`,
    );
  };

  const handlePrivacyChange = async (setting) => {
    const newValue = !privacy[setting];
    setPrivacy({ ...privacy, [setting]: newValue });
    await saveUserSettings("privacy", setting, newValue);
    toast.success(
      `${setting === "showEmail" ? "Email visibility" : "Phone visibility"} ${newValue ? "enabled" : "disabled"}`,
    );
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getStatusIcon = (status) => {
    const config = statusConfig[status] || statusConfig.Pending;
    const IconComponent = config.icon;
    return <IconComponent size={16} color={config.color} />;
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.Pending;
    return (
      <span className={`order-status-badge ${status?.toLowerCase()}`}>
        {getStatusIcon(status)}
        <span>{config.label}</span>
      </span>
    );
  };

  if (authLoading) return <div className="loading-spinner">Loading...</div>;
  if (!user || user.role === "Admin") return null;

  const stats = {
    totalOrders: orders.length,
    totalSpent: orders.reduce(
      (sum, order) => sum + (order.TotalAmount || 0),
      0,
    ),
    pendingOrders: orders.filter((o) => o.Status === "Pending").length,
    completedOrders: orders.filter((o) => o.Status === "Delivered").length,
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: FiUser },
    { id: "orders", label: "My Orders", icon: FiPackage },
    { id: "settings", label: "Settings", icon: FiSettings },
  ];

  const getFullAddress = () => {
    const parts = [
      profileData.addressLine1,
      profileData.addressLine2,
      profileData.city,
      profileData.state,
      profileData.postalCode,
      profileData.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Not provided";
  };

  return (
    <>
      <div className="dashboard-container">
        <div className="dashboard-sidebar">
          <div className="user-profile-card">
            <div className="user-avatar">
              {getAvatarLetter(user?.name || user?.userName || "U")}
            </div>
            <h3 className="case-sensitive-name">
              {formatNameCaseSensitive(user?.name || user?.userName)}
            </h3>
            <p className="user-email">{user?.email}</p>
            <div className="user-badge">
              {user?.customerType || "Retail"} Customer
              {isSocialLogin && (
                <span className="social-badge"> • Social Login</span>
              )}
              {!user?.emailVerified && (
                <span className="verification-badge unverified">
                  ⚠️ Unverified
                </span>
              )}
              {user?.emailVerified && (
                <span className="verification-badge verified">✅ Verified</span>
              )}
            </div>
          </div>

          <nav className="dashboard-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`dashboard-nav-item ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon />
                <span>{tab.label}</span>
              </button>
            ))}
            <button
              className="dashboard-nav-item logout"
              onClick={handleLogout}
            >
              <FiLogOut />
              <span>Logout</span>
            </button>
          </nav>
        </div>

        <div className="dashboard-main">
          <AnimatePresence mode="wait">
            {activeTab === "overview" && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="dashboard-content"
              >
                <h1>
                  Welcome back, {getFirstName(user?.name || user?.userName)}!
                </h1>
                <p className="dashboard-subtitle">
                  Here's what's happening with your account
                </p>

                {/* Email Verification Banner */}
                {user && !user.emailVerified && (
                  <div className="email-verification-banner">
                    <div className="verification-banner-content">
                      <div className="verification-icon">
                        <FiMail />
                      </div>
                      <div className="verification-text">
                        <h4>Verify Your Email Address</h4>
                        <p>
                          Please verify your email address to access all
                          features. Check your inbox for the verification link.
                        </p>
                      </div>
                      <div className="verification-actions">
                        <button
                          className="resend-verification-btn"
                          onClick={handleResendVerification}
                          disabled={resending}
                        >
                          {resending ? "Sending..." : "Resend Email"}
                        </button>
                        <button
                          className="refresh-verification-btn"
                          onClick={handleCheckVerification}
                          disabled={checkingStatus}
                        >
                          <FiRefreshCw
                            className={checkingStatus ? "spin" : ""}
                          />
                          Check Status
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiShoppingBag />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.totalOrders}</h3>
                      <p>Total Orders</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiDollarSign />
                    </div>
                    <div className="stat-info">
                      <h3>R{stats.totalSpent.toLocaleString()}</h3>
                      <p>Total Spent</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiPackage />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.pendingOrders}</h3>
                      <p>Pending Orders</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiHeart />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.completedOrders}</h3>
                      <p>Completed Orders</p>
                    </div>
                  </div>
                </div>

                <div className="profile-section">
                  <div className="section-header">
                    <h2>Profile Information</h2>
                    {!isEditing && (
                      <button
                        className="edit-btn"
                        onClick={() => setIsEditing(true)}
                      >
                        <FiEdit2 /> Edit Profile
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="profile-edit-form">
                      <div className="form-group">
                        <label>
                          Full Name <span className="required">*</span>
                        </label>
                        <div className="input-icon-wrapper">
                          <FiUser className="input-icon" />
                          <input
                            type="text"
                            value={profileData.userName}
                            onChange={(e) =>
                              setProfileData({
                                ...profileData,
                                userName: e.target.value,
                              })
                            }
                            placeholder="Enter your full name"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <div className="input-icon-wrapper">
                          <FiMail className="input-icon" />
                          <input
                            type="email"
                            value={profileData.email}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>
                          Phone Number <span className="required">*</span>
                        </label>
                        <div className="input-icon-wrapper">
                          <FiPhone className="input-icon" />
                          <input
                            type="tel"
                            value={profileData.phone}
                            onChange={(e) =>
                              setProfileData({
                                ...profileData,
                                phone: e.target.value,
                              })
                            }
                            placeholder="Enter your phone number"
                            required
                          />
                        </div>
                      </div>

                      <h3 className="section-subtitle">Shipping Address</h3>

                      <div className="form-group google-address-group">
                        <label>
                          Search Address <span className="required">*</span>
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <GoogleAddressAutocomplete
                              onAddressSelect={handleAddressFromGoogle}
                              initialValue={
                                profileData.addressLine1 && profileData.city
                                  ? `${profileData.addressLine1}, ${profileData.city}, ${profileData.state}`
                                  : ""
                              }
                              placeholder="Start typing your address"
                              required={false}
                            />
                          </div>
                          <button
                            type="button"
                            className="map-picker-btn"
                            onClick={() => setShowMapPicker(true)}
                            title="Pick from map"
                          >
                            Map
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          Street Address <span className="required">*</span>
                        </label>
                        <div className="input-icon-wrapper">
                          <FiHome className="input-icon" />
                          <input
                            type="text"
                            value={profileData.addressLine1}
                            onChange={(e) =>
                              setProfileData({
                                ...profileData,
                                addressLine1: e.target.value,
                              })
                            }
                            placeholder="House number and street name"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Address Line 2 (Optional)</label>
                        <div className="input-icon-wrapper">
                          <FiMapPin className="input-icon" />
                          <input
                            type="text"
                            value={profileData.addressLine2}
                            onChange={(e) =>
                              setProfileData({
                                ...profileData,
                                addressLine2: e.target.value,
                              })
                            }
                            placeholder="Apartment, suite, unit, etc."
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group half">
                          <label>
                            City <span className="required">*</span>
                          </label>
                          <div className="input-icon-wrapper">
                            <FiMapPin className="input-icon" />
                            <input
                              type="text"
                              value={profileData.city}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  city: e.target.value,
                                })
                              }
                              placeholder="City"
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group half">
                          <label>
                            State/Province <span className="required">*</span>
                          </label>
                          <div className="select-icon-wrapper">
                            <FiGlobe className="select-icon" />
                            <select
                              value={profileData.state}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  state: e.target.value,
                                })
                              }
                              required
                            >
                              <option value="">Select Province</option>
                              {saProvinces.map((province) => (
                                <option key={province} value={province}>
                                  {province}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group half">
                          <label>
                            Postal Code <span className="required">*</span>
                          </label>
                          <div className="input-icon-wrapper">
                            <FiMapPin className="input-icon" />
                            <input
                              type="text"
                              value={profileData.postalCode}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  postalCode: e.target.value,
                                })
                              }
                              placeholder="Postal code"
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group half">
                          <label>
                            Country <span className="required">*</span>
                          </label>
                          <div className="select-icon-wrapper">
                            <FiGlobe className="select-icon" />
                            <select
                              value={profileData.country}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  country: e.target.value,
                                })
                              }
                              required
                            >
                              {countries.map((country) => (
                                <option key={country} value={country}>
                                  {country}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          className="save-btn"
                          onClick={handleUpdateProfile}
                        >
                          <FiSave /> Save Changes
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => setIsEditing(false)}
                        >
                          <FiX /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="profile-info-grid">
                      <div className="info-item">
                        <FiUser className="info-icon" />
                        <div>
                          <label>Full Name</label>
                          <p className="case-sensitive-name">
                            {formatNameCaseSensitive(
                              user?.name || user?.userName,
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="info-item">
                        <FiMail className="info-icon" />
                        <div>
                          <label>Email Address</label>
                          <p>{user?.email}</p>
                        </div>
                      </div>
                      <div className="info-item">
                        <FiPhone className="info-icon" />
                        <div>
                          <label>Phone Number</label>
                          <p>{profileData.phone || "Not provided"}</p>
                        </div>
                      </div>
                      <div className="info-item">
                        <FiHome className="info-icon" />
                        <div>
                          <label>Shipping Address</label>
                          <p>{getFullAddress()}</p>
                        </div>
                      </div>
                      <div className="info-item">
                        <FiCalendar className="info-icon" />
                        <div>
                          <label>Member Since</label>
                          <p>
                            {new Date(
                              user?.createdAt || Date.now(),
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === "orders" && (
              <motion.div
                key="orders"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="dashboard-content"
              >
                <h1>My Orders</h1>
                <p className="dashboard-subtitle">
                  Track and manage your orders
                </p>

                {/* Order Statistics */}
                <div className="order-stats-grid">
                  <div className="order-stat-card total">
                    <div className="stat-value">{orderStats.total}</div>
                    <div className="stat-label">Total Orders</div>
                  </div>
                  <div className="order-stat-card pending">
                    <div className="stat-value">{orderStats.pending}</div>
                    <div className="stat-label">Pending</div>
                  </div>
                  <div className="order-stat-card processing">
                    <div className="stat-value">{orderStats.processing}</div>
                    <div className="stat-label">Processing</div>
                  </div>
                  <div className="order-stat-card shipped">
                    <div className="stat-value">{orderStats.shipped}</div>
                    <div className="stat-label">Shipped</div>
                  </div>
                  <div className="order-stat-card delivered">
                    <div className="stat-value">{orderStats.delivered}</div>
                    <div className="stat-label">Delivered</div>
                  </div>
                </div>

                {/* Filters and Search */}
                <div className="order-filters">
                  <div className="filter-buttons">
                    <button
                      className={`filter-btn ${orderFilter === "all" ? "active" : ""}`}
                      onClick={() => setOrderFilter("all")}
                    >
                      All
                    </button>
                    <button
                      className={`filter-btn ${orderFilter === "pending" ? "active" : ""}`}
                      onClick={() => setOrderFilter("pending")}
                    >
                      Pending
                    </button>
                    <button
                      className={`filter-btn ${orderFilter === "processing" ? "active" : ""}`}
                      onClick={() => setOrderFilter("processing")}
                    >
                      Processing
                    </button>
                    <button
                      className={`filter-btn ${orderFilter === "shipped" ? "active" : ""}`}
                      onClick={() => setOrderFilter("shipped")}
                    >
                      Shipped
                    </button>
                    <button
                      className={`filter-btn ${orderFilter === "delivered" ? "active" : ""}`}
                      onClick={() => setOrderFilter("delivered")}
                    >
                      Delivered
                    </button>
                  </div>
                  <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search by order number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {loading ? (
                  <div className="loading-spinner">Loading orders...</div>
                ) : filteredOrders.length === 0 ? (
                  <div className="empty-state">
                    <FiPackage className="empty-icon" />
                    <h3>No orders found</h3>
                    <p>
                      {searchTerm || orderFilter !== "all"
                        ? "Try changing your search or filter criteria"
                        : "Start shopping to see your orders here"}
                    </p>
                    {(searchTerm || orderFilter !== "all") && (
                      <button
                        className="clear-filters-btn"
                        onClick={() => {
                          setSearchTerm("");
                          setOrderFilter("all");
                        }}
                      >
                        Clear Filters
                      </button>
                    )}
                    {!searchTerm && orderFilter === "all" && (
                      <button
                        className="shop-now-btn"
                        onClick={() => navigate("/shop")}
                      >
                        Shop Now
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="orders-list">
                    {filteredOrders.map((order) => {
                      const StatusIcon =
                        statusConfig[order.Status]?.icon || FiPackage;
                      const statusColor =
                        statusConfig[order.Status]?.color || "#666";
                      return (
                        <div key={order.OrderID} className="order-card">
                          <div className="order-header">
                            <div className="order-info-left">
                              <span className="order-number">
                                #{order.OrderNumber}
                              </span>
                              <span className="order-date">
                                {new Date(order.CreatedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <div
                              className={`order-status-badge ${order.Status?.toLowerCase()}`}
                            >
                              <StatusIcon size={14} color={statusColor} />
                              <span>{order.Status || "Pending"}</span>
                            </div>
                          </div>
                          <div className="order-body">
                            <div className="order-details">
                              <div className="detail-row">
                                <span>Total Amount:</span>
                                <strong>
                                  R{order.TotalAmount?.toLocaleString()}
                                </strong>
                              </div>
                              <div className="detail-row">
                                <span>Payment Status:</span>
                                <span className="payment-status">
                                  {order.PaymentStatus || "Pending"}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span>Payment Method:</span>
                                <span>
                                  {(
                                    order.PaymentMethod || "Manual"
                                  ).toUpperCase()}
                                </span>
                              </div>
                              <div className="detail-row">
                                <span>Delivery:</span>
                                <strong>
                                  {order.ShippingMethodName ||
                                    order.ShippingMethodCode ||
                                    "Standard delivery"}
                                </strong>
                              </div>
                              <div className="detail-row">
                                <span>Delivery Status:</span>
                                <span className="payment-status">
                                  {order.ShippingStatus || "Not shipped"}
                                </span>
                              </div>
                            </div>
                            <button
                              className="view-order-btn"
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowOrderModal(true);
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === "settings" && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="dashboard-content"
              >
                <h1>Settings</h1>
                <p className="dashboard-subtitle">
                  Manage your account preferences
                </p>

                {/* Notification Preferences */}
                <div className="settings-section">
                  <h3>
                    <FiBell /> Notification Preferences
                  </h3>
                  <div className="settings-option">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={notifications.email}
                        onChange={() => handleNotificationChange("email")}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>
                      <FiMailIcon /> Email notifications for order updates
                    </span>
                  </div>
                  <div className="settings-option">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={notifications.sms}
                        onChange={() => handleNotificationChange("sms")}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>
                      <FiSmartphone /> SMS notifications for order status
                    </span>
                  </div>
                  <div className="settings-option">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={notifications.orderUpdates}
                        onChange={() =>
                          handleNotificationChange("orderUpdates")
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>
                      <FiPackage /> Order status changes
                    </span>
                  </div>
                  <div className="settings-option">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={notifications.promotional}
                        onChange={() => handleNotificationChange("promotional")}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>
                      <FiBell /> Promotional offers and deals
                    </span>
                  </div>
                </div>

                {/* Privacy & Security */}
                <div className="settings-section">
                  <h3>
                    <FiShield /> Privacy & Security
                  </h3>
                  <div className="settings-option">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={privacy.showEmail}
                        onChange={() => handlePrivacyChange("showEmail")}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>Show email on profile</span>
                  </div>
                  <div className="settings-option">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={privacy.showPhone}
                        onChange={() => handlePrivacyChange("showPhone")}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>Show phone number on profile</span>
                  </div>
                  {!isSocialLogin ? (
                    <button
                      className="settings-btn password-btn"
                      onClick={() => setShowPasswordModal(true)}
                    >
                      <FiLock /> Change Password
                    </button>
                  ) : (
                    <div className="social-login-info">
                      <FiLock />
                      <span>
                        Password management is not available for social login
                        accounts. Please use{" "}
                        {user?.provider || "Google/Facebook"} to sign in.
                      </span>
                    </div>
                  )}
                </div>

                {/* Data & Privacy */}
                <div className="settings-section">
                  <h3>
                    <FiDatabase /> Data & Privacy
                  </h3>
                  <button
                    className="settings-btn danger-btn"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <FiTrash2 /> Delete Account
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
          <div
            className="modal-content order-details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Order Details</h2>
              <button
                className="modal-close"
                onClick={() => setShowOrderModal(false)}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {/* Order info */}
              <div className="order-info-section">
                <div className="info-row">
                  <span>Order Number:</span>
                  <strong>#{selectedOrder.OrderNumber}</strong>
                </div>
                <div className="info-row">
                  <span>Order Date:</span>
                  <span>
                    {new Date(selectedOrder.CreatedAt).toLocaleString()}
                  </span>
                </div>
                <div className="info-row">
                  <span>Status:</span>
                  {getStatusBadge(selectedOrder.Status)}
                </div>
                <div className="info-row">
                  <span>Payment Status:</span>
                  <span className="payment-status">
                    {selectedOrder.PaymentStatus || "Pending"}
                  </span>
                </div>
                <div className="info-row">
                  <span>Total Amount:</span>
                  <strong>
                    R{selectedOrder.TotalAmount?.toLocaleString()}
                  </strong>
                </div>
                <div className="info-row">
                  <span>Delivery Method:</span>
                  <strong>
                    {selectedOrder.ShippingMethodName ||
                      selectedOrder.ShippingMethodCode ||
                      "Standard delivery"}
                  </strong>
                </div>
                <div className="info-row">
                  <span>Delivery Status:</span>
                  <span>{selectedOrder.ShippingStatus || "Not shipped"}</span>
                </div>
                <div className="info-row">
                  <span>Shipping Fee:</span>
                  <span>
                    R{selectedOrder.ShippingFee?.toLocaleString() || 0}
                  </span>
                </div>
              </div>

              {/* Order Timeline */}
              <div className="order-timeline">
                <h4>Order Timeline</h4>
                <div className="timeline-items">
                  <div className="timeline-item completed">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Order Placed</div>
                      <div className="timeline-date">
                        {new Date(selectedOrder.CreatedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`timeline-item ${selectedOrder.ProcessedAt ? "completed" : selectedOrder.Status === "Processing" || selectedOrder.Status === "Shipped" || selectedOrder.Status === "Delivered" ? "active" : ""}`}
                  >
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Processing</div>
                      <div className="timeline-date">
                        {selectedOrder.ProcessedAt
                          ? new Date(selectedOrder.ProcessedAt).toLocaleString()
                          : selectedOrder.Status === "Processing" ||
                              selectedOrder.Status === "Shipped" ||
                              selectedOrder.Status === "Delivered"
                            ? "In progress"
                            : "Pending"}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`timeline-item ${selectedOrder.ShippedAt ? "completed" : selectedOrder.Status === "Shipped" || selectedOrder.Status === "Delivered" ? "active" : ""}`}
                  >
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Shipped</div>
                      <div className="timeline-date">
                        {selectedOrder.ShippedAt
                          ? new Date(selectedOrder.ShippedAt).toLocaleString()
                          : selectedOrder.Status === "Shipped" ||
                              selectedOrder.Status === "Delivered"
                            ? "In transit"
                            : "Pending"}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`timeline-item ${selectedOrder.DeliveredAt ? "completed" : selectedOrder.Status === "Delivered" ? "active" : ""}`}
                  >
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-title">Delivered</div>
                      <div className="timeline-date">
                        {selectedOrder.DeliveredAt
                          ? new Date(selectedOrder.DeliveredAt).toLocaleString()
                          : selectedOrder.Status === "Delivered"
                            ? "Out for delivery"
                            : "Pending"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tracking Information */}
              {selectedOrder.TrackingNumber && (
                <div className="tracking-info">
                  <h4>Tracking Information</h4>
                  <div className="tracking-details">
                    <div className="tracking-row">
                      <span>Tracking Number:</span>
                      <strong>{selectedOrder.TrackingNumber}</strong>
                    </div>
                    {selectedOrder.TrackingUrl && (
                      <div className="tracking-row">
                        <a
                          href={selectedOrder.TrackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tracking-link"
                        >
                          Track your package →
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="cancel-modal-btn"
                onClick={() => setShowOrderModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && !isSocialLogin && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Change Password</h2>
              <button
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
                disabled={isChangingPassword}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {passwordError && (
                <div className="modal-error-message">{passwordError}</div>
              )}
              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <FiLock className="input-icon" />
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    placeholder="Enter current password"
                    disabled={isChangingPassword}
                  />
                  <button
                    type="button"
                    className="password-eye"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    disabled={isChangingPassword}
                  >
                    {showCurrentPassword ? (
                      <FiEyeOff size={16} />
                    ) : (
                      <FiEye size={16} />
                    )}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <FiLock className="input-icon" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value,
                      })
                    }
                    placeholder="Enter new password (min. 6 characters)"
                    disabled={isChangingPassword}
                  />
                  <button
                    type="button"
                    className="password-eye"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={isChangingPassword}
                  >
                    {showNewPassword ? (
                      <FiEyeOff size={16} />
                    ) : (
                      <FiEye size={16} />
                    )}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input-wrapper">
                  <FiLock className="input-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value,
                      })
                    }
                    placeholder="Confirm new password"
                    disabled={isChangingPassword}
                  />
                  <button
                    type="button"
                    className="password-eye"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isChangingPassword}
                  >
                    {showConfirmPassword ? (
                      <FiEyeOff size={16} />
                    ) : (
                      <FiEye size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-modal-btn"
                onClick={() => setShowPasswordModal(false)}
                disabled={isChangingPassword}
              >
                Cancel
              </button>
              <button
                className="save-modal-btn"
                onClick={handleChangePassword}
                disabled={passwordLoading || isChangingPassword}
              >
                {passwordLoading ? <>Changing...</> : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h2>Delete Account</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation("");
                }}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <svg
                  viewBox="0 0 24 24"
                  width="48"
                  height="48"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3>Warning: This action cannot be undone!</h3>
                <p>Deleting your account will:</p>
                <ul>
                  <li>Deactivate your account immediately</li>
                  <li>Remove your access to all orders and data</li>
                  <li>Prevent you from logging in</li>
                  <li>
                    Your data will be archived but not permanently deleted
                  </li>
                </ul>
                <p className="reactivation-note">
                  Note: You can contact support to reactivate your account if
                  you change your mind.
                </p>
              </div>
              <div className="delete-confirm">
                <label>
                  Type <strong>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type DELETE here"
                  className="delete-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-modal-btn"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation("");
                }}
              >
                Cancel
              </button>
              <button
                className="delete-modal-btn"
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmation !== "DELETE"}
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address Map Picker Modal */}
      <AddressMapPicker
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        onAddressSelect={handleAddressFromMap}
        initialAddress={
          profileData.addressLine1 && profileData.city
            ? `${profileData.addressLine1}, ${profileData.city}, ${profileData.state}`
            : ""
        }
      />
    </>
  );
};

export default Dashboard;
